import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';

/**
 * 가사 모달 컴포넌트
 * - syncLyrics가 있으면 현재 재생 시간에 맞춰 하이라이트
 * - lyrics만 있으면 전체 가사 스크롤 표시
 * - songId가 현재 재생 중인 곡과 일치할 때만 하이라이트 활성화
 */
function LyricsModal({ lyrics, syncLyrics, songName, artistName, songId, onClose }) {
  const { currentTime, playlist, currentIndex } = useMusicPlayer();
  const lyricsRef = useRef(null);
  const activeLineRef = useRef(null);
  const [parsedLines, setParsedLines] = useState([]);

  // LRC 파싱
  useEffect(() => {
    if (!syncLyrics) return;
    const lines = syncLyrics.split('\n').filter(l => l.trim()).map(line => {
      const match = line.match(/^\[(\d{2}):(\d{2}(?:\.\d+)?)\](.*)$/);
      if (!match) return null;
      const time = parseInt(match[1]) * 60 + parseFloat(match[2]);
      return { time, text: match[3] };
    }).filter(Boolean);
    setParsedLines(lines);
  }, [syncLyrics]);

  // 싱크 가사 → 현재 줄로 스크롤
  useEffect(() => {
    if (activeLineRef.current && lyricsRef.current) {
      activeLineRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentTime, parsedLines]);

  // 현재 재생 중인 곡과 일치할 때만 하이라이트
  const isPlaying = songId && currentIndex !== null && playlist[currentIndex]?.id === songId;
  const activeIndex = isPlaying ? parsedLines.reduce((acc, line, i) => {
    return currentTime >= line.time ? i : acc;
  }, -1) : -1;

  // ESC 키로 닫기
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const hasSyncLyrics = parsedLines.length > 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      {/* 배경 오버레이 */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* 모달 */}
      <div
        className="relative w-full max-w-lg max-h-[70vh] bg-[#1a2b42]/95 border border-white/10 rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="min-w-0">
            <h3 className="text-white font-semibold text-sm truncate">{songName}</h3>
            <p className="text-slate-400 text-xs truncate mt-0.5">{artistName}</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* 가사 본문 */}
        <div ref={lyricsRef} className="flex-1 overflow-y-auto px-5 py-4 scroll-dark">
          {hasSyncLyrics ? (
            // 싱크 가사
            <div className="space-y-3">
              {parsedLines.map((line, i) => (
                <p
                  key={i}
                  ref={i === activeIndex ? activeLineRef : null}
                  className={`transition-all duration-300 text-sm leading-relaxed ${
                    i === activeIndex
                      ? 'text-sky-300 font-semibold text-base scale-[1.02] origin-left'
                      : i < activeIndex
                        ? 'text-slate-500'
                        : 'text-slate-400'
                  }`}>
                  {line.text}
                </p>
              ))}
            </div>
          ) : lyrics ? (
            // 일반 가사
            <div className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed">
              {lyrics}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-10">가사가 없습니다.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default LyricsModal;
